import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  fileToBase64,
  isFeedbackScreenshotMimeType,
  resolveFeedbackEnvironment,
  submitProductFeedback,
} from '../lib/productFeedbackBot';

export function useProductFeedback(): {
  opened: boolean;
  submitting: boolean;
  open: () => void;
  onClose: () => void;
  onSubmit: (values: { feedback: string; screenshot: File | null }) => Promise<void>;
} {
  const medplum = useMedplum();
  const location = useLocation();
  const [opened, setOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setOpened(false);
    setSubmitting(false);
  }, []);

  const onSubmit = useCallback(
    async (values: { feedback: string; screenshot: File | null }) => {
      setSubmitting(true);
      try {
        let screenshot: { dataBase64: string; mimeType: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif'; filename: string } | undefined;
        if (values.screenshot) {
          const mimeType = values.screenshot.type === 'image/jpg' ? 'image/jpeg' : values.screenshot.type;
          if (!isFeedbackScreenshotMimeType(mimeType)) {
            throw new Error('unsupported screenshot type');
          }
          screenshot = {
            dataBase64: await fileToBase64(values.screenshot),
            mimeType,
            filename: values.screenshot.name,
          };
        }
        const result = await submitProductFeedback(medplum, {
          feedback: values.feedback,
          pagePath: (location.pathname || '/').slice(0, 200),
          environment: resolveFeedbackEnvironment(),
          screenshot,
        });
        if (!result.ok) {
          throw new Error('not ok');
        }
        notifications.show({ color: 'green', message: 'Feedback submitted. Thank you.' });
        reset();
      } catch {
        notifications.show({ color: 'red', message: 'Could not submit feedback.' });
        setSubmitting(false);
      }
    },
    [location.pathname, medplum, reset],
  );

  return {
    opened,
    submitting,
    open: () => setOpened(true),
    onClose: () => {
      if (!submitting) reset();
    },
    onSubmit,
  };
}
